import { useCallback, useEffect, useRef } from 'react';
import { ScreenEventType } from 'src/validators/Events/Screen';
import { useEventTracking } from './useEventTracking';
import { useRouter } from 'next/router';
import { usePersistState } from './usePersistState';

interface ScreenView {
  event: string;
  screenInstanceId?: string;
  screenType?: ScreenEventType;
}

const MUTE_SCROLL = true;

export function useScrollTracking() {
  const isBrowser = typeof window !== 'undefined';
  const { track } = useEventTracking();
  const lastTrackedPercentage = useRef<number>(0);
  const currentScreenRef = useRef<ScreenView | null>(null);

  const router = useRouter();
  const routeKey = `screen_instance_id_${router.pathname}`;
  const [screenInstanceId] = usePersistState(routeKey, '');

  const trackScroll = useCallback(
    (screenType: ScreenEventType) => {
      if (!isBrowser) return;

      currentScreenRef.current = {
        event: screenType.toString(),
        screenInstanceId,
        screenType: ScreenEventType[screenType],
      };
    },
    [isBrowser, screenInstanceId],
  );

  useEffect(() => {
    if (!isBrowser) return;

    let timeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (!currentScreenRef.current) return;

      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const scrollHeight = document.body.scrollHeight;
        const scrollPosition = window.scrollY;
        const viewportHeight = window.innerHeight;
        const maxScroll = scrollHeight - viewportHeight;
        const scrollPercentage = (scrollPosition / maxScroll) * 100;

        const scrollHasChangedEnough =
          !MUTE_SCROLL &&
          Math.abs(scrollPercentage - lastTrackedPercentage.current) > 5;

        if (scrollHasChangedEnough) {
          void track(ScreenEventType[currentScreenRef.current.screenType], {
            screen_instance_id: currentScreenRef.current.screenInstanceId,
            scroll_percentage: scrollPercentage,
            // TODO: Add scrollPosition , maxScroll
          });
          lastTrackedPercentage.current = scrollPercentage;
        }
      }, 1000);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
      currentScreenRef.current = null;
    };
  }, [track, isBrowser]);

  return { trackScroll };
}
